import { Controller } from "@hotwired/stimulus"

// A styled dropdown for the unit name.
//
// This was a native <datalist>, which browsers render in their own chrome —
// positioned wherever they like, unstyleable, and nothing like the pickers
// used everywhere else in the app.
//
// Local rather than server-backed like the ingredient combobox: there are
// about thirty units, so filtering in the DOM is instant and needs no
// endpoint. Still a free text field, so an unusual unit is not blocked.
export default class extends Controller {
  static targets = ["input", "list", "option", "amount"]
  static values = { suggestions: Object, measure: String }

  connect() {
    this.activeIndex = -1
    this.closeOnOutside = (event) => {
      if (this.element.contains(event.target)) return
      this.close()
    }
    document.addEventListener("click", this.closeOnOutside)

    // A food switched between weight and volume is offered different units —
    // ml and litre against g and kg — so the list follows the toggle. Every
    // unit is already in the DOM, tagged with the measure it belongs to, so
    // this hides rather than fetches.
    //
    // On the window: the toggle is on the form and each unit row is its own
    // controller, so there is no element one could bubble to that all of
    // them share and the form does not.
    this.onMeasureChange = (event) => {
      this.measureValue = event.detail.measure
    }
    window.addEventListener("measure:changed", this.onMeasureChange)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutside)
    window.removeEventListener("measure:changed", this.onMeasureChange)
  }

  // Stimulus calls this whenever measureValue is assigned, including the
  // first time — so the list is correct on connect without a special case.
  measureValueChanged() {
    this.applyMeasure()
    if (!this.listTarget?.hidden) this.filter()
  }

  // A unit tagged with the OTHER measure is not merely hidden from view: it
  // must not be findable by typing or reachable by arrow key either, so it
  // is taken out of the running here and every other method reads
  // availableOptions rather than optionTargets.
  applyMeasure() {
    this.optionTargets.forEach((option) => {
      option.dataset.unavailable =
        option.dataset.measure && option.dataset.measure !== this.measureValue
          ? "true"
          : ""
    })
  }

  get availableOptions() {
    return this.optionTargets.filter((option) => !option.dataset.unavailable)
  }

  open() {
    this.listTarget.hidden = false
    this.filter()
  }

  close() {
    this.listTarget.hidden = true
    this.activeIndex = -1
  }

  filter() {
    const term = this.inputTarget.value.trim().toLowerCase()
    let visible = 0

    this.optionTargets.forEach((option) => {
      const match =
        !option.dataset.unavailable && option.dataset.value.includes(term)
      option.hidden = !match
      if (match) visible++
    })

    // Nothing to choose from is not worth a panel — an unlisted unit is
    // still perfectly valid, just typed.
    this.listTarget.hidden = visible === 0
    this.activeIndex = -1
    this.highlight()
    this.suggest()
  }

  choose(event) {
    this.inputTarget.value = event.currentTarget.dataset.value
    this.close()
    this.suggest()
    this.inputTarget.focus()
  }

  // Fill in the amount for a unit that has one by definition — 15 for a
  // tablespoon, in whatever the food is measured in. A
  // tablespoon is 15 ml whatever is in it; a slice is not, so those suggest
  // nothing. Only ever fills a BLANK field.
  suggest() {
    if (!this.hasAmountTarget || this.amountTarget.value) return

    const amount = this.suggestionsValue[this.inputTarget.value.trim().toLowerCase()]
    if (amount) this.amountTarget.value = amount
  }

  navigate(event) {
    if (event.key === "Escape") return this.close()

    const options = this.availableOptions.filter((option) => !option.hidden)
    if (options.length === 0) return

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      this.listTarget.hidden = false
      const step = event.key === "ArrowDown" ? 1 : -1
      this.activeIndex = (this.activeIndex + step + options.length) % options.length
      this.highlight()
    } else if (event.key === "Enter" && this.activeIndex >= 0) {
      // Only when something is highlighted — otherwise Enter should submit
      // the form as usual.
      event.preventDefault()
      options[this.activeIndex].click()
    }
  }

  highlight() {
    const options = this.availableOptions.filter((option) => !option.hidden)
    options.forEach((option, index) => {
      option.classList.toggle("bg-sunken", index === this.activeIndex)
    })
    options[this.activeIndex]?.scrollIntoView({ block: "nearest" })
  }
}
